import { useEffect, useState } from 'react';
import { buildingRelatedForms, EFormType, EFormTypeKey, IForm, propertyGroupRelatedForms } from '../../models/form/form';
import { Box, styled } from '@mui/system';
import AuthService from '../../services/auth/AuthService';
import FormService from '../../services/form/FormService';
import AddNewFormModal from '../../components/form/modal/AddNewFormModal';
import FormListElement from '../../components/form/FormListElement';
import EditOnly from '../../components/permissions/EditOnly';
import { IconButton, List, ListItemButton, ListItemText, MenuItem, Paper, Select, Typography, LinearProgress } from '@mui/material';
import { theme } from '../../styles/themes/mainTheme';
import { useTranslation } from 'react-i18next';
import FormInput from '../../components/input/form/FormInput';
import { ELanguage } from '../../models/enums/language';
import { availableLanguages, eLanguages } from '../../i18n';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BuildingService from '../../services/building/BuildingService';
import IAdminPageBuildingDetail from '../../models/adminPage/adminPageBuildingDetail';
import { useNavigate } from 'react-router-dom';
import { IPropertyGroup } from '../../models/propertyGroup';
import propertyGroupService from '../../services/propertyGroupService/PropertyGroupService';


const Toolbar = styled(Paper)(() => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1%',
    width: '100%',
}));

const FormPageWrapper = styled(Paper)(() => ({
    display: 'flex',
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: '2%',
    marginTop: '1%'
}));
const FormListWrapper = styled(Box)(() => ({
    display: 'flex',
    width: '85%',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '2%',
    paddingBottom: 0
}));

const FormTemplatesPage = () => {
    const { t } = useTranslation(['form']);
    const navigate = useNavigate();
    const [isShowCreateForm, setIsShowCreateForm] = useState<boolean>(false);
    const [updateForms, setUpdateForms] = useState<number>(0);
    const [selectedType, setSelectedType] = useState<EFormType>(Number(Object.keys(EFormType)[0]));
    const [selectedLanguages, setSelectedLanguages] = useState<ELanguage[]>([]);
    const [forms, setForms] = useState<IForm[]>([]);

    const [buildings, setBuildings] = useState<IAdminPageBuildingDetail[]>([]);
    const [selectedBuildingId, setSelectedBuildingId] = useState<number>(-1);

    const [propertyGroups, setPropertyGroups] = useState<IPropertyGroup[]>([]);
    const [selectedPropertyGroupId, setSelectedPropertyGroupId] = useState<number>(-1);

    const filteredForms = forms.filter((form) => {
        const isTypeMatch = form.formType === selectedType;
        const isLanguageMatch = selectedLanguages.includes(form.lang) || selectedLanguages.length === 0;
        const isBuildingMatch = buildingRelatedForms.includes(selectedType) ? form.buildings.some((b) => b.buildingId === selectedBuildingId) : true;
        const isPropertyGroupMatch = propertyGroupRelatedForms.includes(selectedType) ? form.propertyGroups.some((pg) => pg.propertyGroupId === selectedPropertyGroupId) : true;

        return isTypeMatch && isLanguageMatch && isBuildingMatch && isPropertyGroupMatch;
    });

    useEffect(() => {
        BuildingService.GetBuildingsDetails().then((buildings) => {
            setBuildings(buildings)
            setSelectedBuildingId(buildings[0].id)
        });
        propertyGroupService.GetPropertyGroups().then((propertyGroups) => {
            setPropertyGroups(propertyGroups)
            setSelectedPropertyGroupId(propertyGroups[0].id)
        });
    }, []);

    useEffect(() => {
        FormService.GetFormTemplates(AuthService.user?.companyId || 0, AuthService.guid)
            .then((formsReceived) => setForms(formsReceived || []))
    }, [updateForms]);

    const selectType = (type: EFormType) => {
        setSelectedType(type);
    };

    const onFormClick = (form: IForm) => {
        navigate("edit", { state: { form } })
    }

    const onSetFormActive = (form: IForm) => {
        const formBuildingIndex = form.buildings.findIndex((b) => b.buildingId == selectedBuildingId)
        const formPropertyGroupIndex = form.propertyGroups.findIndex((pg) => pg.propertyGroupId == selectedPropertyGroupId)
        if (formBuildingIndex < 0 && formPropertyGroupIndex < 0) return;

        if (formBuildingIndex >= 0) {
            form.buildings[formBuildingIndex].isActive = !form.buildings[formBuildingIndex].isActive;
        }

        if (formPropertyGroupIndex >= 0) {
            form.propertyGroups[formPropertyGroupIndex].isActive = !form.propertyGroups[formPropertyGroupIndex].isActive;
        }

        FormService.PutFormTemplate(form)
            .then(() => setUpdateForms(updateForms + 1))
    }

    const updateLanguage = (value: string | number[]): void => {
        const ids =
            typeof value === "string"
                ? value.split(",").map((v) => Number(v))
                : value;
        setSelectedLanguages(ids);
    };

    return (
        <Box>
            <Toolbar>
                <FormInput
                    sx={{ minWidth: 250 }}
                    label={t("form:language")}
                >
                    <Select
                        notched
                        multiple
                        defaultValue={[-1]}
                        value={selectedLanguages}
                        onChange={(event) =>
                            updateLanguage(event.target.value)
                        }
                        id="language-select"
                    >
                        {availableLanguages.map((lang) => (
                            <MenuItem key={lang} value={eLanguages.get(lang)}>
                                {t(`common:header.${ELanguage[eLanguages.get(lang) as ELanguage].toLowerCase()}`)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormInput>

                {buildingRelatedForms.includes(selectedType) && (
                    <FormInput
                        sx={{ minWidth: 250 }}
                        label={t("form:building")}
                    >
                        <Select
                            notched
                            fullWidth
                            defaultValue={-1}
                            value={selectedBuildingId}
                            onChange={(event) =>
                                setSelectedBuildingId(Number(event.target.value))
                            }
                            id="building-select"
                        >
                            {buildings.map((building) => (
                                <MenuItem
                                    key={`building_${building.id}`}
                                    value={building.id}
                                >
                                    {building.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormInput>
                )}

                {propertyGroupRelatedForms.includes(selectedType) && (
                    <FormInput
                        sx={{ minWidth: 250 }}
                        label={t("form:propertyGroup")}
                    >
                        <Select
                            notched
                            fullWidth
                            defaultValue={-1}
                            value={selectedPropertyGroupId}
                            onChange={(event) =>
                                setSelectedPropertyGroupId(Number(event.target.value))
                            }
                            id="propertyGroup-select"
                        >
                            {propertyGroups.map((propertyGroup) => (
                                <MenuItem
                                    key={`propertyGroup_${propertyGroup.id}`}
                                    value={propertyGroup.id}
                                >
                                    {propertyGroup.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormInput>
                )}

                <EditOnly>
                    <IconButton onClick={() => setIsShowCreateForm(true)}><AddCircleOutlineIcon /></IconButton>
                </EditOnly>
                <AddNewFormModal isShowCreate={isShowCreateForm} setIsShowCreate={setIsShowCreateForm} setUpdateForms={setUpdateForms} />
            </Toolbar>

            <FormPageWrapper>
                <List sx={{ width: '15%', borderRight: '1px solid #000' }}>
                    <Typography variant='subtitle1' fontWeight={600}>
                        {t('form:formTypes')}
                    </Typography>
                    {Object.keys(EFormType).filter(formType => isNaN(Number(formType)) && formType !== "Undefined").map((formType) => (
                        <ListItemButton sx={{ backgroundColor: Number(EFormType[formType as EFormTypeKey]) === selectedType ? theme.palette.grey[100] : "default" }}
                            key={formType}
                            onClick={() => selectType(Number(EFormType[formType as EFormTypeKey]))}>
                            <ListItemText primary={t(`form:type.${formType}`)} />
                        </ListItemButton>
                    ))}
                </List>

                <FormListWrapper>
                    {
                        filteredForms.sort((a, b) => {
                            const groupComparison = a.groupId - b.groupId;
                            const languageComparison = a.lang.toString().localeCompare(b.lang.toString());

                            return groupComparison || languageComparison;
                        }).map((form) => (
                            <FormListElement key={form.id} form={form} onFormClick={() => onFormClick(form)}
                                setUpdateForms={setUpdateForms} onSetFormActive={onSetFormActive} buildingId={selectedBuildingId} propertyGroupId={selectedPropertyGroupId} />
                        ))
                    }
                </FormListWrapper>
            </FormPageWrapper >
        </Box >
    );
};
export default FormTemplatesPage;
